/**
 * Staff Affiliate admin — list, create, edit, and drill into an affiliate.
 *
 * Kept intentionally compact (single file) — the ledger + payments actions
 * live on the detail row inline rather than a dedicated sub-page, so staff
 * can approve/mark-paid in the same flow as they browse.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, Plus, X, ExternalLink, Copy, Download, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/utils/apiError';
import useHomePath from '@/hooks/useHomePath';
import { AmbassadorLeaderboard } from '@/components/affiliate/AmbassadorLeaderboard';

const empty = {
  name: '', slug: '', commission_percent: 10,
  contact_email: '', contact_phone: '', notes: '',
  login_email: '', login_password: '',
};

export default function StaffAffiliates() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const [affs, setAffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [ledger, setLedger] = useState({ open: false, affiliate: null, rows: [], busy: false });
  const [showPromote, setShowPromote] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ email: '', name: '', commission_percent: 10 });
  const [promoting, setPromoting] = useState(false);
  // Pending applications from the public /become-an-ambassador form.
  const [applications, setApplications] = useState([]);

  const load = async () => {
    setLoading(true);
    // Fetch pending applications alongside the main list so staff sees
    // "N applications waiting" in the header the moment they land.
    const [rAff, rApps] = await Promise.all([
      fetch(`${API_URL}/api/staff/affiliates`, { credentials: 'include' }),
      fetch(`${API_URL}/api/staff/ambassador-applications?status=pending`, { credentials: 'include' }),
    ]);
    setAffs(rAff.ok ? await rAff.json() : []);
    setApplications(rApps.ok ? await rApps.json() : []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const decideApplication = async (app, action, opts = {}) => {
    if (action === 'reject' && !window.confirm(`Reject ${app.name}'s application?`)) return;
    const r = await fetch(`${API_URL}/api/staff/ambassador-applications/${app.id}/decision`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, commission_percent: opts.rate ?? 10, slug: opts.slug }),
    });
    const data = await r.json();
    if (!r.ok) { toast.error(formatApiError(data) || 'Decision failed'); return; }
    toast.success(action === 'approve' ? `${app.name} approved — /join/${data.affiliate.slug}` : `${app.name} rejected`);
    await load();
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/affiliates`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json()) || 'Create failed');
      toast.success('Ambassador created');
      setShowAdd(false); setForm(empty);
      await load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const updateRate = async (aff, next) => {
    const r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ commission_percent: parseFloat(next) }),
    });
    if (r.ok) { toast.success('Rate updated'); await load(); }
    else toast.error(formatApiError(await r.json()) || 'Update failed');
  };

  const updateBrandRate = async (aff, brand, next) => {
    // Server merges — passing just the one brand won't wipe the others.
    const value = parseFloat(next);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      toast.error('Rate must be 0–100');
      return;
    }
    const r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ commission_rates: { [brand]: value } }),
    });
    if (r.ok) { toast.success(`${brand} rate → ${value}%`); await load(); }
    else toast.error(formatApiError(await r.json()) || 'Update failed');
  };

  const toggleStatus = async (aff) => {
    const nextStatus = aff.status === 'active' ? 'suspended' : 'active';
    const r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: nextStatus }),
    });
    if (r.ok) { toast.success(`Ambassador ${nextStatus}`); await load(); }
  };

  const adjustCredit = async (aff) => {
    // Prompt-based tiny modal — deliberately zero-ceremony since staff
    // will only touch this rarely (correcting a mistake or seeding a
    // starting balance). Full modal would be overkill for a one-field
    // input + optional note.
    const raw = window.prompt(
      `Adjust credit for ${aff.name}.\n\nCurrent balance: £${(aff.credit_balance || 0).toFixed(2)}\n\nEnter a signed amount (positive to credit, negative to debit):`,
    );
    if (raw === null || raw.trim() === '') return;
    const amount = parseFloat(raw);
    if (Number.isNaN(amount) || amount === 0) {
      toast.error('Amount must be a non-zero number.');
      return;
    }
    const notes = window.prompt('Note (recommended — records why the adjustment was made):') || '';
    const r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}/adjust-credit`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, notes }),
    });
    const body = await r.json();
    if (!r.ok) { toast.error(formatApiError(body) || 'Adjustment failed'); return; }
    toast.success(`Balance is now £${body.new_balance.toFixed(2)}`);
    await load();
  };

  const redeemCredit = async (aff) => {
    // Same lightweight prompt-flow. Amount + optional target order id +
    // note. The staff typically types the material-order id off the
    // Xero draft or Ox invoice they're issuing.
    const raw = window.prompt(
      `Redeem credit against an invoice for ${aff.name}.\n\nAvailable: £${(aff.credit_balance || 0).toFixed(2)}\n\nAmount to redeem:`,
    );
    if (raw === null || raw.trim() === '') return;
    const amount = parseFloat(raw);
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error('Amount must be a positive number.');
      return;
    }
    if (amount > (aff.credit_balance || 0) + 0.005) {
      toast.error(`Only £${(aff.credit_balance || 0).toFixed(2)} available.`);
      return;
    }
    const ref = window.prompt('Order / invoice reference (optional — e.g. an installer material order id):') || '';
    const notes = window.prompt('Note (optional — where the credit was applied):') || '';
    const r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}/redeem-credit`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, reference_type: 'installer_material_orders', reference_id: ref || null, notes }),
    });
    const body = await r.json();
    if (!r.ok) { toast.error(formatApiError(body) || 'Redemption failed'); return; }
    toast.success(`£${amount.toFixed(2)} redeemed — balance is now £${body.new_balance.toFixed(2)}`);
    await load();
  };

  const openLedger = async (aff) => {
    setLedger({ open: true, affiliate: aff, rows: [], busy: true });
    const r = await fetch(`${API_URL}/api/staff/affiliate-commissions?affiliate_id=${aff.id}`, { credentials: 'include' });
    setLedger({ open: true, affiliate: aff, rows: r.ok ? await r.json() : [], busy: false });
  };

  const markStatus = async (cid, status) => {
    const r = await fetch(`${API_URL}/api/staff/affiliate-commissions/${cid}/status`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!r.ok) return toast.error('Update failed');
    toast.success(`Marked ${status}`);
    if (ledger.affiliate) openLedger(ledger.affiliate);
    await load();
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/join/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied — share it anywhere');
  };

  // Upload / replace an affiliate's logo. Called from the per-row logo cell.
  // Uses the pre-existing POST /staff/affiliates/{id}/logo endpoint. Cache-
  // bust the <img> src after upload by tacking a query string on so staff
  // see the new logo without a hard refresh.
  const uploadLogo = async (aff, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}/logo`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    if (!r.ok) {
      toast.error(formatApiError(await r.json()) || 'Logo upload failed');
      return;
    }
    toast.success('Logo updated');
    await load();
  };

  // Manually attach an existing installer (by email) to an Ambassador.
  // Uses the /staff/affiliates/{id}/link-installer endpoint. If the
  // installer is already linked to a different Ambassador, backend
  // returns 409 — we ask the staff user whether to override.
  const linkExistingInstaller = async (aff) => {
    const email = window.prompt(`Link an existing installer to ${aff.name}.\n\nEnter their account email:`);
    if (!email) return;
    let r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}/link-installer`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    });
    if (r.status === 409) {
      const override = window.confirm('This installer is already linked to another Ambassador. Re-assign them?');
      if (!override) return;
      r = await fetch(`${API_URL}/api/staff/affiliates/${aff.id}/link-installer`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), override: true }),
      });
    }
    if (!r.ok) {
      toast.error(formatApiError(await r.json()) || 'Could not link installer');
      return;
    }
    const data = await r.json();
    toast.success(`${data.name || email} linked to ${aff.name}`);
    await load();
  };

  // Promote an existing installer into an Ambassador. The installer keeps
  // their installer login + access — we just stamp `affiliate_own_id`
  // on their user record and mint a fresh Ambassador partner profile.
  const promoteInstaller = async () => {
    if (!promoteForm.email.trim()) return toast.error('Installer email is required');
    setPromoting(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/affiliates/promote-installer`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: promoteForm.email.trim(),
          name: promoteForm.name.trim() || undefined,
          commission_percent: parseFloat(promoteForm.commission_percent) || 10,
        }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json()) || 'Promote failed');
      const data = await r.json();
      toast.success(data.already_promoted
        ? `${data.name} is already an Ambassador — refreshed.`
        : `${data.name} promoted to Ambassador. Share link: /join/${data.slug}`);
      setShowPromote(false);
      setPromoteForm({ email: '', name: '', commission_percent: 10 });
      await load();
    } catch (e) { toast.error(e.message); } finally { setPromoting(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b px-3 sm:px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-gray-100 rounded shrink-0"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Ox Ambassador Portal</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <a
            href={`${API_URL}/api/staff/affiliate-commissions/export.csv`}
            className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md border-2 border-gray-200 text-gray-700 hover:border-gray-300"
            data-testid="export-commissions-csv"
          >
            <Download className="w-4 h-4" /> CSV
          </a>
          <Button variant="outline" size="sm" onClick={() => setShowPromote(true)} data-testid="promote-installer-open">
            Promote installer
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="new-affiliate">
            <Plus className="w-4 h-4 mr-1" /> New Ambassador
          </Button>
          <button onClick={() => navigate(homePath)} className="p-1.5 hover:bg-gray-100 rounded" title="Home">
            <Home className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        <AmbassadorLeaderboard />
        {/* Pending applications from /become-an-ambassador — pinned to the
            top of the page so staff can approve/reject in-flight without
            navigating away. */}
        {applications.length > 0 && (
          <Card className="p-4 mb-4 border-2 border-amber-300 bg-amber-50/50" data-testid="ambassador-applications-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-amber-900">
                {applications.length} application{applications.length === 1 ? '' : 's'} awaiting review
              </h2>
              <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">from /become-an-ambassador</span>
            </div>
            <div className="space-y-2">
              {applications.map((app) => (
                <div key={app.id} className="rounded-lg border bg-white p-3 flex flex-wrap items-start gap-3" data-testid={`application-${app.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-semibold text-gray-900">{app.name}{app.company_name ? ` · ${app.company_name}` : ''}</p>
                    <p className="text-xs text-gray-500">{app.email}{app.phone ? ` · ${app.phone}` : ''} · audience: {app.audience || '—'}</p>
                    {app.pitch && <p className="text-sm text-gray-700 mt-1 italic">"{app.pitch}"</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => decideApplication(app, 'approve')}
                      data-testid={`approve-app-${app.id}`}
                    >Approve</Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => decideApplication(app, 'reject')}
                      data-testid={`reject-app-${app.id}`}
                    >Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : affs.length === 0 ? (
          <Card className="p-10 text-center text-gray-500">No Ambassadors yet — click "New Ambassador" to invite your first partner.</Card>
        ) : (
          <div className="space-y-3">
            {affs.map((a) => (
              <Card key={a.id} data-testid={`affiliate-${a.id}`} className="p-4">
                <div className="flex flex-wrap gap-4 items-start">
                  <LogoCell aff={a} onPick={(f) => uploadLogo(a, f)} />
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-900">{a.name}</h3>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${a.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{a.status}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-2">
                      <span>/join/<code className="text-[#7A0B7A]">{a.slug}</code></span>
                      <button onClick={() => copyLink(a.slug)} className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700"><Copy className="w-3 h-3" /> copy</button>
                      <a href={`/join/${a.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700"><ExternalLink className="w-3 h-3" /> preview</a>
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div><span className="text-gray-500 text-xs block">Customers</span><span className="font-semibold">{a.stats?.customers_count || 0}</span></div>
                      <div><span className="text-gray-500 text-xs block">Gross sales</span><span className="font-semibold">£{(a.stats?.gross_sales || 0).toFixed(2)}</span></div>
                      <div><span className="text-gray-500 text-xs block">Pending</span><span className="font-semibold text-amber-700">£{(a.stats?.commission_pending || 0).toFixed(2)}</span></div>
                      <div><span className="text-gray-500 text-xs block">Paid</span><span className="font-semibold text-emerald-700">£{(a.stats?.commission_paid || 0).toFixed(2)}</span></div>
                    </div>
                    {/* Credit balance chip — sits alongside stats so staff
                        can see who's got redeemable credit on their next
                        invoice at a glance. */}
                    <div className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-md border-2 border-emerald-300 bg-emerald-50 px-3 py-1.5 max-w-full" data-testid={`credit-balance-${a.id}`}>
                      <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Credit balance</span>
                      <span className="text-lg font-bold text-emerald-800">£{(a.credit_balance || 0).toFixed(2)}</span>
                      <button
                        onClick={() => adjustCredit(a)}
                        data-testid={`adjust-credit-${a.id}`}
                        className="text-[11px] font-semibold text-emerald-800 underline hover:text-emerald-900"
                      >Adjust</button>
                      <button
                        onClick={() => redeemCredit(a)}
                        data-testid={`redeem-credit-${a.id}`}
                        className="text-[11px] font-semibold text-emerald-800 underline hover:text-emerald-900"
                        disabled={(a.credit_balance || 0) <= 0}
                      >Redeem</button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-full sm:w-auto">
                    {/* Per-brand commission rates — commit on blur. Server
                        merges so editing one brand won't wipe the others.
                        Empty rates fall back to the "Fallback %" input
                        (legacy `commission_percent`) so single-rate legacy
                        rows still work. */}
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      {[
                        { id: 'probound', label: 'Bound', accent: 'text-[#2F5E96] border-[#2F5E96]' },
                        { id: 'proepoxy', label: 'Epoxy', accent: 'text-[#7A0B7A] border-[#7A0B7A]' },
                        { id: 'prodecor', label: 'Decor', accent: 'text-[#D5AB09] border-[#D5AB09]' },
                      ].map((b) => (
                        <label key={b.id} className={`inline-flex items-center gap-1 rounded-md border-2 px-1.5 py-0.5 bg-white ${b.accent}`}>
                          <span className="font-bold text-[10px] uppercase">{b.label}</span>
                          <Input
                            type="number" step="0.5" min="0" max="100"
                            defaultValue={a.commission_rates?.[b.id] ?? a.commission_percent}
                            onBlur={(e) => {
                              const cur = a.commission_rates?.[b.id] ?? a.commission_percent;
                              if (e.target.value !== String(cur)) updateBrandRate(a, b.id, e.target.value);
                            }}
                            className="w-14 h-6 text-xs px-1 border-0 shadow-none focus-visible:ring-0"
                            data-testid={`rate-${b.id}-${a.id}`}
                          />
                          <span className="text-[9px] text-gray-400">%</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="text-[10px] text-gray-400 uppercase font-bold">Fallback %</label>
                      <Input
                        type="number" step="0.5" min="0" max="100"
                        defaultValue={a.commission_percent}
                        onBlur={(e) => e.target.value !== String(a.commission_percent) && updateRate(a, e.target.value)}
                        className="w-16 h-7 text-xs"
                        data-testid={`rate-${a.id}`}
                        title="Applies when a brand doesn't have its own rate set."
                      />
                      <Button variant="outline" size="sm" onClick={() => openLedger(a)} data-testid={`ledger-${a.id}`}>Ledger</Button>
                      <Button variant="outline" size="sm" onClick={() => linkExistingInstaller(a)} data-testid={`link-installer-${a.id}`}>Link installer</Button>
                      <Button variant="outline" size="sm" onClick={() => toggleStatus(a)}>{a.status === 'active' ? 'Suspend' : 'Activate'}</Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">New Ambassador</h2>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div><Label>Name / company</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="aff-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Commission %</Label><Input type="number" step="0.5" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: parseFloat(e.target.value) || 0 })} data-testid="aff-rate" /></div>
                <div><Label>Slug (optional)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto from name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
                <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
              </div>
              <div className="border-t pt-3">
                <p className="text-xs text-gray-500 mb-2">Optional: give them a login to the affiliate dashboard now (you can add this later).</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Login email</Label><Input value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} data-testid="aff-login-email" /></div>
                  <div><Label>Password</Label><Input type="password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} data-testid="aff-login-pass" /></div>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button onClick={submit} disabled={saving} className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="aff-submit">{saving ? 'Creating…' : 'Create'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promote existing installer modal */}
      {showPromote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowPromote(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Promote installer to Ambassador</h2>
              <button onClick={() => setShowPromote(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              They keep their existing installer login &amp; access — this just gives them
              their own Ambassador share link, commission %, and access to the Ambassador dashboard
              from the same login.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Installer email</Label>
                <Input
                  type="email"
                  value={promoteForm.email}
                  onChange={(e) => setPromoteForm({ ...promoteForm, email: e.target.value })}
                  placeholder="dan@theirtrade.co.uk"
                  data-testid="promote-email"
                />
              </div>
              <div>
                <Label>Display name <span className="text-gray-400 font-normal">(optional — defaults to company)</span></Label>
                <Input
                  value={promoteForm.name}
                  onChange={(e) => setPromoteForm({ ...promoteForm, name: e.target.value })}
                  placeholder="e.g. Ox Resin Bristol"
                  data-testid="promote-name"
                />
              </div>
              <div>
                <Label>Commission %</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={promoteForm.commission_percent}
                  onChange={(e) => setPromoteForm({ ...promoteForm, commission_percent: e.target.value })}
                  data-testid="promote-rate"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPromote(false)}>Cancel</Button>
                <Button
                  onClick={promoteInstaller}
                  disabled={promoting}
                  className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white"
                  data-testid="promote-submit"
                >
                  {promoting ? 'Promoting…' : 'Promote'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ledger */}
      {ledger.open && ledger.affiliate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setLedger({ ...ledger, open: false })}>
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Commissions · {ledger.affiliate.name}</h2>
              <div className="flex items-center gap-2">
                <a href={`${API_URL}/api/staff/affiliate-commissions/export.csv?affiliate_id=${ledger.affiliate.id}`} className="text-xs px-3 py-1.5 rounded border-2 border-gray-200 hover:border-gray-300">CSV</a>
                <button onClick={() => setLedger({ ...ledger, open: false })}><X className="w-5 h-5" /></button>
              </div>
            </div>
            {ledger.busy ? <p className="text-gray-500">Loading…</p> : ledger.rows.length === 0 ? (
              <p className="text-gray-500 text-center py-10">No commissions yet — they'll show up here once one of {ledger.affiliate.name}'s customers has a paid order.</p>
            ) : (
              <div className="space-y-2">
                {ledger.rows.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center gap-3 border rounded-lg p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{r.order_type} · {r.order_id}</p>
                      <p className="text-xs text-gray-500">£{(r.order_value || 0).toFixed(2)} order · {r.commission_rate}% = £{(r.commission_amount || 0).toFixed(2)}</p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      r.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                      r.status === 'approved' ? 'bg-amber-100 text-amber-800' :
                      r.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-200 text-gray-600'
                    }`}>{r.status}</span>
                    <div className="flex gap-1">
                      {r.status !== 'paid' && r.status !== 'cancelled' && (
                        <Button size="sm" variant="outline" onClick={() => markStatus(r.id, 'paid')} data-testid={`mark-paid-${r.id}`}>Mark paid</Button>
                      )}
                      {r.status !== 'cancelled' && r.status !== 'paid' && (
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => markStatus(r.id, 'cancelled')}>Cancel</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── LogoCell ─────────────────────────────────────────────────────────────
// A 56px square that either shows the affiliate's current logo (with a
// hover overlay to replace it) or an empty upload dropzone. Keeps the row
// tidy and lets staff swap a logo in-place without opening a modal.
// Cache-busted with `?t=updated_at` so a re-upload shows the new image.
function LogoCell({ aff, onPick }) {
  const inputRef = useRef(null);
  const bust = aff.updated_at || aff.created_at || '';
  const logoUrl = aff.logo_storage_path
    ? `${API_URL}/api/staff/affiliates/${aff.id}/logo?t=${encodeURIComponent(bust)}`
    : null;

  const validateAndPick = (f) => {
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error('Logo must be PNG, JPG, or WEBP');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error('Logo too large (max 2MB) — please resize and try again');
      return;
    }
    onPick(f);
  };

  return (
    <div className="shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => { validateAndPick(e.target.files?.[0]); e.target.value = ''; }}
        data-testid={`aff-logo-input-${aff.id}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="group relative w-14 h-14 rounded-lg border-2 border-dashed border-gray-200 hover:border-[#7A0B7A] bg-white overflow-hidden inline-flex items-center justify-center"
        title={logoUrl ? 'Click to replace logo' : 'Click to upload logo (PNG/JPG/WEBP, max 2MB)'}
        data-testid={`aff-logo-btn-${aff.id}`}
      >
        {logoUrl ? (
          <img src={logoUrl} alt={aff.name} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <ImageIcon className="w-5 h-5 text-gray-300 group-hover:text-[#7A0B7A]" />
        )}
        <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Upload className="w-4 h-4 text-white" />
        </span>
      </button>
    </div>
  );
}
